import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ItemCard from './ItemCard';

const BuildContainer = styled.div`
  margin-top: 1rem;
`;

const BuildSection = styled(motion.div)`
  margin-bottom: 2.5rem;
  background-color: rgba(26, 13, 58, 0.3);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const SectionTitle = styled.h4`
  font-size: 1.2rem;
  padding: 1rem;
  background-color: rgba(46, 23, 96, 0.7);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    width: 12px;
    height: 12px;
    background-color: ${props => getSectionColor(props.type)};
    border-radius: 50%;
  }
`;

const SectionContent = styled.div`
  padding: 1rem;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BuildSummary = styled.div`
  background-color: rgba(46, 23, 96, 0.3);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
  
  h4 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--accent-color);
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.25rem;
    }
  }
`;

const ItemsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

// Helper function to get color based on section type
function getSectionColor(type) {
  switch(type?.toLowerCase()) {
    case 'starter':
      return '#4caf50'; // Green
    case 'relic':
      return '#ff9800'; // Orange
    case 'items':
      return '#2196f3'; // Blue
    default:
      return 'var(--accent-color)';
  }
}

// Calculate added stats from items only
function calculateItemStats(items) {
  // Initialize stats at 0 - we only want to show stats added by items
  const stats = {
    physicalPower: 0,
    magicalPower: 0,
    health: 0,
    physicalProtection: 0,
    magicalProtection: 0,
    movementSpeed: 0,
    criticalStrikeChance: 0,
    attackSpeed: 0,
    lifeSteal: 0,
    cooldownReduction: 0,
    penetration: 0,
  };
  
  // Sum up item stats
  [...items].forEach(item => {
    if (item.stats) {
      item.stats.forEach(stat => {
        if (stat.StatTag === 'PhysicalPower' || stat.StatTag === 'Strength') {
          stats.physicalPower += stat.Value;
        }
        if (stat.StatTag === 'MagicalPower' || stat.StatTag === 'Intelligence') {
          stats.magicalPower += stat.Value;
        }
        if (stat.StatTag === 'MaxHealth') {
          stats.health += stat.Value;
        }
        if (stat.StatTag === 'PhysicalProtection') {
          stats.physicalProtection += stat.Value;
        }
        if (stat.StatTag === 'MagicalProtection') {
          stats.magicalProtection += stat.Value;
        }
        if (stat.StatTag === 'MovementSpeed') {
          stats.movementSpeed += stat.Value;
        }
        if (stat.StatTag === 'CriticalStrikeChance') {
          stats.criticalStrikeChance += stat.Value;
        }
        if (stat.StatTag === 'AttackSpeed') {
          stats.attackSpeed += stat.Value;
        }
        if (stat.StatTag === 'LifeSteal') {
          stats.lifeSteal += stat.Value;
        }
        if (stat.StatTag === 'CooldownReduction') {
          stats.cooldownReduction += stat.Value;
        }
        if (stat.StatTag === 'FlatPenetration' || stat.StatTag === 'PercentagePenetration') {
          stats.penetration += stat.Value;
        }
      });
    }
  });
  
  return stats;
}

const BuildDisplay = ({ build }) => {
  const { god, starter, relic, items } = build;
  
  // Calculate stats from items only
  const itemStats = calculateItemStats([starter, relic, ...items]);
  
  // Only display stats that have values
  const statsToDisplay = [];
  
  if (itemStats.physicalPower > 0) {
    statsToDisplay.push({ name: 'Physical Power', value: `+${itemStats.physicalPower}` });
  }
  
  if (itemStats.magicalPower > 0) {
    statsToDisplay.push({ name: 'Magical Power', value: `+${itemStats.magicalPower}` });
  }
  
  if (itemStats.health > 0) {
    statsToDisplay.push({ name: 'Health', value: `+${itemStats.health}` });
  }
  
  if (itemStats.physicalProtection > 0) {
    statsToDisplay.push({ name: 'Physical Protection', value: `+${itemStats.physicalProtection}` });
  }
  
  if (itemStats.magicalProtection > 0) {
    statsToDisplay.push({ name: 'Magical Protection', value: `+${itemStats.magicalProtection}` });
  }
  
  if (itemStats.movementSpeed > 0) {
    statsToDisplay.push({ name: 'Movement Speed', value: `+${itemStats.movementSpeed}` });
  }
  
  if (itemStats.criticalStrikeChance > 0) {
    statsToDisplay.push({ name: 'Critical Chance', value: `+${itemStats.criticalStrikeChance}%` });
  }
  
  if (itemStats.attackSpeed > 0) {
    statsToDisplay.push({ name: 'Attack Speed', value: `+${itemStats.attackSpeed}` });
  }
  
  if (itemStats.lifeSteal > 0) {
    statsToDisplay.push({ name: 'Life Steal', value: `+${itemStats.lifeSteal}%` });
  }
  
  if (itemStats.cooldownReduction > 0) {
    statsToDisplay.push({ name: 'Cooldown Reduction', value: `+${itemStats.cooldownReduction}%` });
  }
  
  if (itemStats.penetration > 0) {
    statsToDisplay.push({ name: 'Penetration', value: `+${itemStats.penetration}` });
  }
  
  return (
    <BuildContainer>
      <BuildSummary>
        <h4>Build Summary</h4>
        <p>This completely random build includes a starter item, a relic, and six tier 3 items for {god.name}. Embrace the chaos!</p>
        <ul>
          {statsToDisplay.map((stat, index) => (
            <li key={index}>{stat.name}: {stat.value}</li>
          ))}
          {statsToDisplay.length === 0 && <li>No stat bonuses from these items!</li>}
        </ul>
      </BuildSummary>
      
      <BuildSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SectionTitle type="starter">Starter</SectionTitle>
        <SectionContent>
          <ItemCard item={starter} type="Starter" />
        </SectionContent>
      </BuildSection>
      
      <BuildSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SectionTitle type="relic">Relic</SectionTitle>
        <SectionContent>
          <ItemCard item={relic} type="Relic" />
        </SectionContent>
      </BuildSection>
      
      <BuildSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <SectionTitle type="items">Items (6)</SectionTitle>
        <SectionContent>
          <ItemsGrid>
            {items.map((item, index) => (
              <ItemCard 
                key={index} 
                item={item} 
                animationDelay={0.2 + (index * 0.1)} 
              />
            ))}
          </ItemsGrid>
        </SectionContent>
      </BuildSection>
    </BuildContainer>
  );
};

export default BuildDisplay; 